import { CSSProperties } from 'react';
import { CartesianMarkerProps } from '@nivo/core';
import { LegendProps } from '@nivo/legends';

import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';
import type { FinancialValueDisplayPreference } from '@/types/financialValueDisplay';

type LegendAnchor = 'bottom' | 'bottom-left' | 'bottom-right' | 'left' | 'right' | 'top' | 'top-left' | 'top-right';
type BarLegendDataFrom = 'indexes' | 'keys';
type BottomLegendOptions = {
    anchor?: LegendAnchor;
    translateY?: number;
};

export const humanizeChartLabel = (value: unknown) =>
    String(value ?? '')
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\bEbitda\b/gi, 'EBITDA')
        .replace(/\bEbit\b/gi, 'EBIT')
        .replace(/\bEps\b/gi, 'EPS')
        .replace(/\b[A-Z]{2,}\b/g, (match) => match)
        .replace(/\b\w/g, (match) => match.toUpperCase());

export const truncateChartLabel = (value: unknown, maxLength = 18) => {
    const label = humanizeChartLabel(value);

    if (label.length <= maxLength) {
        return label;
    }

    return `${label.slice(0, Math.max(0, maxLength - 3)).trim()}...`;
};

export const normalizeChartPrimitive = (value: unknown): number | string | null | undefined => {
    if (value === null || value === undefined) {
        return value;
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    if (typeof value === 'number' || typeof value === 'string') {
        return value;
    }

    return String(value);
};

export const getChartFormatting = (config?: ReportComponentConfig) => ({
    showAverageLine: Boolean(config?.formatting?.showAverageLine),
    showAxisLabels: Boolean(config?.formatting?.showAxisLabels),
    showLegend: Boolean(config?.formatting?.showLegend),
    showZeroValues: Boolean(config?.formatting?.showZeroValues),
});

export const formatCompactChartValue = (
    value: number | string | null | undefined,
    formatValue: (value: number | string | null | undefined) => string,
    financialValueDisplay?: FinancialValueDisplayPreference,
) => {
    if (financialValueDisplay) {
        return formatValue(value);
    }

    const numericValue = typeof value === 'number' ? value : Number(value);

    if (!Number.isFinite(numericValue) || Math.abs(numericValue) < 1000) {
        return formatValue(value);
    }

    const formatted = formatValue(value);
    const prefix = formatted.trim().startsWith('$') ? '$' : '';
    const suffix = formatted.trim().endsWith('%') ? '%' : '';
    const compact = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: Math.abs(numericValue) >= 1_000_000 ? 1 : 0,
        notation: 'compact',
    }).format(numericValue);

    return `${prefix}${compact}${suffix}`;
};

export const getCartesianChartMargin = ({
    showAxisLabels,
    showLegend,
    horizontal = false,
}: {
    horizontal?: boolean;
    showAxisLabels: boolean;
    showLegend: boolean;
}) => ({
    top: 24,
    right: horizontal ? 32 : 24,
    bottom: showLegend ? 88 : showAxisLabels ? 56 : 40,
    left: horizontal ? (showAxisLabels ? 112 : 88) : showAxisLabels ? 72 : 56,
});

export const getCircularChartMargin = (showLegend: boolean) => ({
    top: 18,
    right: 24,
    bottom: showLegend ? 76 : 18,
    left: 24,
});

const buildBottomLegend = ({ anchor = 'bottom', translateY = 60 }: BottomLegendOptions = {}): LegendProps => ({
    anchor,
    direction: 'row',
    itemDirection: 'left-to-right',
    itemHeight: 18,
    itemOpacity: 1,
    itemTextColor: 'currentColor',
    itemWidth: 120,
    itemsSpacing: 8,
    justify: false,
    symbolShape: 'circle',
    symbolSize: 10,
    toggleSerie: true,
    translateX: 0,
    translateY,
});

export const getBottomLegend = (options?: BottomLegendOptions): LegendProps[] => [buildBottomLegend(options)];

export const getBarBottomLegend = (
    options: BottomLegendOptions & { dataFrom: BarLegendDataFrom },
): Array<LegendProps & { dataFrom: BarLegendDataFrom }> => [
    {
        ...buildBottomLegend(options),
        dataFrom: options.dataFrom,
    },
];

export const getAverageMarker = ({
    axis,
    color,
    formatValue,
    financialValueDisplay,
    label = 'Average',
    value,
}: {
    axis: 'x' | 'y';
    color: string;
    formatValue: (value: number | string | null | undefined) => string;
    financialValueDisplay?: FinancialValueDisplayPreference;
    label?: string;
    value?: number;
}): CartesianMarkerProps[] => {
    if (!Number.isFinite(value)) {
        return [];
    }

    return [
        {
            axis,
            value: value as number,
            legend: `${label} ${formatCompactChartValue(value, formatValue, financialValueDisplay)}`,
            legendOrientation: axis === 'y' ? ('horizontal' as const) : ('vertical' as const),
            legendPosition: axis === 'y' ? ('top-right' as const) : ('top-left' as const),
            lineStyle: {
                stroke: color,
                strokeDasharray: '4 4',
                strokeWidth: 1.5,
            } as CSSProperties,
            textStyle: {
                fill: color,
                fontSize: 11,
                fontWeight: 600,
            } as CSSProperties,
        },
    ];
};

export const averageNumericValues = (values: unknown[]) => {
    const numbers = values.map(Number).filter((value) => Number.isFinite(value));

    if (!numbers.length) {
        return undefined;
    }

    return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
};

export const filterZeroBarRows = <T extends Record<string, unknown>>(
    data: T[],
    metricKeys: string[],
    showZeroValues: boolean,
) => {
    if (showZeroValues) {
        return data;
    }

    return data.filter((row) => metricKeys.some((key) => Number(row[key] ?? 0) !== 0));
};

export const filterZeroSeriesPoints = <T extends { data: Array<{ y: unknown }> }>(
    series: T[],
    showZeroValues: boolean,
) => {
    if (showZeroValues) {
        return series;
    }

    return series
        .map((serie) => ({
            ...serie,
            data: serie.data.filter((point) => Number(point.y ?? 0) !== 0),
        }))
        .filter((serie) => serie.data.length > 0);
};

export const filterZeroValueItems = <T extends { value: unknown }>(data: T[], showZeroValues: boolean) => {
    if (showZeroValues) {
        return data;
    }

    return data.filter((slice) => Number(slice.value ?? 0) !== 0);
};

export const filterZeroPieSlices = filterZeroValueItems;
